import { searchService, type SearchResult } from '@/api/services/searchService';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSearch } from '@/hooks/useSearch';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  ArrowRight,
  Clock,
  CreditCard,
  Download,
  FileBox,
  FileText,
  LayoutDashboard,
  Loader2,
  Monitor,
  Palette,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  Target,
  User,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  href: string;
  keywords: string[];
  type?: 'navigation';
}

interface SearchResultItemWithType extends SearchResult {
  icon: React.ReactNode;
  type: 'account' | 'transaction' | 'goal' | 'document' | 'party';
  href: string;
}

type CombinedSearchItem = SearchItem | SearchResultItemWithType;

interface SearchCategory {
  title: string;
  items: CombinedSearchItem[];
  type: 'navigation' | 'accounts' | 'transactions' | 'goals' | 'documents' | 'parties';
}

interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMobile?: boolean;
}

const RECENT_SEARCHES_KEY = 'fintecy.search.recent';
const MAX_RECENT_SEARCHES = 5;

function loadRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((q) => typeof q === 'string') : [];
  } catch {
    return [];
  }
}

function persistRecentSearch(query: string): string[] {
  const trimmed = query.trim();
  if (!trimmed) return loadRecentSearches();
  const next = [trimmed, ...loadRecentSearches().filter((q) => q !== trimmed)].slice(
    0,
    MAX_RECENT_SEARCHES
  );
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export const SearchCommand = ({ open, onOpenChange, isMobile = false }: SearchCommandProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState<SearchItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchCategories, setSearchCategories] = useState<SearchCategory[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => loadRecentSearches());
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);

  const { searchResults, isLoading, error, search, clearResults } = useSearch({
    debounceMs: 300,
    minQueryLength: 2,
    enabled: open,
  });

  // Get icon for search result type
  const getResultIcon = useCallback((type: string, size = 16) => {
    switch (type) {
      case 'account':
        return <CreditCard size={size} />;
      case 'transaction':
        return <Receipt size={size} />;
      case 'goal':
        return <Target size={size} />;
      case 'document':
        return <FileText size={size} />;
      case 'party':
        return <Users size={size} />;
      default:
        return <Search size={size} />;
    }
  }, []);

  // Generate href for search result based on type and id
  const getResultHref = useCallback((type: string, id: string): string => {
    switch (type) {
      case 'account':
        return `/accounts/${id}`;
      case 'goal':
        return `/planning/goals/${id}`;
      case 'document':
        return `/vault/${id}`;
      case 'transaction':
        return `/banking/transactions/${id}`;
      case 'party':
        // Parties don't have detail pages yet
        return '';
      default:
        // For other types, return empty string or a default route
        return '';
    }
  }, []);

  // Define all searchable items - memoized to prevent infinite re-renders
  const searchItems: SearchItem[] = useMemo(
    () => [
      // Main navigation
      {
        id: 'dashboard',
        title: t('nav.dashboard'),
        description: 'View your accounts and financial overview',
        icon: <LayoutDashboard size={20} />,
        href: '/',
        keywords: ['dashboard', 'home', 'overview', 'accounts', 'balance', 'summary'],
      },
      {
        id: 'goals',
        title: t('nav.goals'),
        description: 'Manage your financial goals',
        icon: <Target size={20} />,
        href: '/planning/goals',
        keywords: ['goals', 'targets', 'savings', 'objectives', 'financial goals'],
      },
      {
        id: 'transactions',
        title: t('nav.transactions'),
        description: 'View and manage your transactions',
        icon: <Receipt size={20} />,
        href: '/banking/transactions',
        keywords: ['transactions', 'payments', 'history', 'transfers', 'expenses'],
      },
      {
        id: 'vault',
        title: t('nav.vault'),
        description: 'Secure document storage',
        icon: <FileBox size={20} />,
        href: '/vault',
        keywords: ['vault', 'documents', 'files', 'storage', 'secure'],
      },
      {
        id: 'settings',
        title: t('nav.settings'),
        description: t('settings.description'),
        icon: <Settings size={20} />,
        href: '/settings',
        keywords: ['settings', 'preferences', 'configuration', 'options'],
      },
      // Settings sub-pages
      {
        id: 'settings-personal',
        title: t('settings.tabs.personal'),
        description: t('settings.personal.description'),
        icon: <User size={16} />,
        href: '/settings/personal',
        keywords: ['personal', 'profile', 'name', 'email', 'information'],
      },
      {
        id: 'settings-preferences',
        title: t('settings.tabs.preferences'),
        description: t('settings.preferences.description'),
        icon: <Palette size={16} />,
        href: '/settings/preferences',
        keywords: ['preferences', 'language', 'theme', 'appearance', 'locale'],
      },
      {
        id: 'settings-sessions',
        title: t('settings.tabs.sessions'),
        description: t('settings.sessions.description'),
        icon: <Monitor size={16} />,
        href: '/settings/sessions',
        keywords: ['sessions', 'devices', 'login', 'security', 'active'],
      },
      {
        id: 'settings-security',
        title: t('settings.tabs.security'),
        description: t('settings.security.description'),
        icon: <ShieldCheck size={16} />,
        href: '/settings/security',
        keywords: ['security', 'password', 'authentication', 'two-factor', '2fa', 'mfa'],
        type: 'navigation' as const,
      },
      {
        id: 'settings-privacy',
        title: t('settings.tabs.privacy'),
        description: t('settings.privacy.description'),
        icon: <Download size={16} />,
        href: '/settings/privacy',
        keywords: ['privacy', 'data', 'export', 'gdpr', 'personal information'],
        type: 'navigation' as const,
      },
    ],
    [t]
  );

  // Filter navigation items and trigger data search
  useEffect(() => {
    if (!query.trim()) {
      setFilteredItems(searchItems);
      setSearchCategories([]);
      setSelectedIndex(0);
      setTotalResults(0);
      clearResults();
      return;
    }

    // Filter navigation items
    const filteredNavItems = searchItems.filter((item) => {
      const searchQuery = query.toLowerCase();
      return (
        item.title.toLowerCase().includes(searchQuery) ||
        item.description?.toLowerCase().includes(searchQuery) ||
        item.keywords.some((keyword) => keyword.toLowerCase().includes(searchQuery))
      );
    });

    setFilteredItems(filteredNavItems);

    // Trigger data search if query is long enough
    if (query.length >= 2) {
      search(query, { limit: 5 }); // Limit results for better UX in search dropdown
    }

    setSelectedIndex(0);
  }, [query, searchItems, search, clearResults]);

  // Process search results into categories
  useEffect(() => {
    if (!searchResults) {
      setSearchCategories([]);
      setTotalResults(filteredItems.length);
      return;
    }

    const categories: SearchCategory[] = [];
    let total = filteredItems.length;

    // Add navigation category if there are filtered navigation items
    if (filteredItems.length > 0) {
      categories.push({
        title: t('search.categories.navigation'),
        items: filteredItems,
        type: 'navigation',
      });
    }

    // Map category keys to result types
    const categoryKeyToType: Record<
      keyof typeof searchResults.results,
      'account' | 'transaction' | 'goal' | 'document' | 'party'
    > = {
      accounts: 'account',
      transactions: 'transaction',
      goals: 'goal',
      documents: 'document',
      parties: 'party',
    };

    // Add data categories with results
    const dataCategories: Array<{
      key: keyof typeof searchResults.results;
      titleKey: string;
      type: SearchCategory['type'];
    }> = [
      { key: 'accounts', titleKey: 'accounts', type: 'accounts' },
      { key: 'transactions', titleKey: 'transactions', type: 'transactions' },
      { key: 'goals', titleKey: 'goals', type: 'goals' },
      { key: 'documents', titleKey: 'documents', type: 'documents' },
      { key: 'parties', titleKey: 'parties', type: 'parties' },
    ];

    dataCategories.forEach(({ key, titleKey, type: categoryType }) => {
      const results = searchResults.results[key];
      if (results && results.length > 0) {
        const itemsWithIcons: SearchResultItemWithType[] = results.map((result) => {
          // Use result.type if available, otherwise infer from category key mapping
          const resultType = (result.type || categoryKeyToType[key]) as
            | 'account'
            | 'transaction'
            | 'goal'
            | 'document'
            | 'party';
          const href = getResultHref(resultType, result.id);
          return {
            ...result,
            icon: getResultIcon(resultType),
            href,
            type: resultType,
          };
        });

        categories.push({
          title: t(`search.categories.${titleKey}`),
          items: itemsWithIcons,
          type: categoryType,
        });

        total += results.length;
      }
    });

    setSearchCategories(categories);
    setTotalResults(total);
  }, [searchResults, filteredItems, t, getResultIcon, getResultHref]);

  const handleSelect = useCallback(
    (item: CombinedSearchItem) => {
      if (item.href) {
        if (query.trim().length >= 2) {
          setRecentSearches(persistRecentSearch(query));
        }
        navigate(item.href);
        onOpenChange(false);
        setQuery('');
      }
    },
    [navigate, onOpenChange, query]
  );

  const handleViewAll = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setRecentSearches(persistRecentSearch(trimmed));
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    onOpenChange(false);
    setQuery('');
  }, [navigate, onOpenChange, query]);

  const handleRecentSelect = useCallback((term: string) => {
    setQuery(term);
  }, []);

  const clearRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch {
      /* ignore */
    }
    setRecentSearches([]);
  }, []);

  // Get all items for keyboard navigation
  const allItems = useMemo(() => {
    const items: CombinedSearchItem[] = [];
    searchCategories.forEach((category) => {
      items.push(...category.items);
    });
    return items.length > 0 ? items : filteredItems;
  }, [searchCategories, filteredItems]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = Math.min(prev + 1, allItems.length - 1);
            // Scroll the selected item into view
            setTimeout(() => {
              const selectedElement = document.querySelector(`[data-search-index="${newIndex}"]`);
              if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 0);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = Math.max(prev - 1, 0);
            // Scroll the selected item into view
            setTimeout(() => {
              const selectedElement = document.querySelector(`[data-search-index="${newIndex}"]`);
              if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 0);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (allItems[selectedIndex]) {
            handleSelect(allItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, allItems, selectedIndex, onOpenChange, handleSelect]);

  // Reset search when dialog opens/closes and focus input
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
    } else {
      // Focus the appropriate input based on mobile/desktop
      setTimeout(() => {
        const inputRef = isMobile ? mobileInputRef : desktopInputRef;
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open, isMobile]);

  const showEmptyState = !query.trim();

  const recentSearchesBlock =
    showEmptyState && recentSearches.length > 0 ? (
      <div>
        <div className='flex items-center justify-between px-2 py-1'>
          <span className='text-muted-foreground text-xs font-semibold tracking-wide uppercase'>
            {t('search.recentSearches')}
          </span>
          <button
            type='button'
            onClick={clearRecentSearches}
            className='text-muted-foreground hover:text-foreground text-xs'
          >
            {t('common.delete')}
          </button>
        </div>
        <div className='space-y-1'>
          {recentSearches.map((term) => (
            <Button
              key={term}
              variant='ghost'
              className='h-auto w-full justify-start gap-3 p-3'
              onClick={() => handleRecentSelect(term)}
            >
              <Clock className='text-muted-foreground size-4 shrink-0' />
              <span className='truncate'>{term}</span>
            </Button>
          ))}
        </div>
      </div>
    ) : null;

  const viewAllFooter =
    query.trim().length >= 2 ? (
      <button
        type='button'
        onClick={handleViewAll}
        className='hover:bg-secondary text-foreground flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm transition-colors'
      >
        <span className='flex items-center gap-2'>
          <Search className='text-muted-foreground size-4' />
          {t('search.viewAllResults')}
        </span>
        <span className='text-muted-foreground flex items-center gap-1 truncate'>
          <span className='max-w-[12rem] truncate'>&ldquo;{query.trim()}&rdquo;</span>
          <ArrowRight className='size-4 shrink-0' />
        </span>
      </button>
    ) : null;

  if (isMobile) {
    return (
      <div
        className={cn(
          'bg-background fixed inset-0 z-50 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        <div className='flex h-full flex-col'>
          <div className='flex h-14 items-center gap-3 border-b px-4'>
            {/* Search Input Field - matching MainLayout style */}
            <div className='relative flex-1'>
              <Search className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2' />
              <Input
                ref={mobileInputRef}
                placeholder={t('search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className='h-9 pr-3 pl-9'
                autoFocus
              />
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onOpenChange(false)}
              className='text-muted-foreground p-2'
              aria-label='Close search'
            >
              <X className='h-5 w-5' />
            </Button>
          </div>
          <div className='flex-1 overflow-y-auto p-4'>
            {isLoading && (
              <div className='text-muted-foreground flex items-center justify-center gap-2 p-4'>
                <Loader2 size={16} className='animate-spin' />
                <span>{t('search.searching')}</span>
              </div>
            )}

            {error && (
              <div className='text-destructive flex items-center justify-center gap-2 p-4'>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {!isLoading && !error && totalResults === 0 && query.length >= 2 && (
              <div className='text-muted-foreground flex flex-col items-center justify-center gap-2 p-8'>
                <Search size={32} />
                <div className='text-center'>
                  <div className='font-medium'>{t('search.noResults')}</div>
                  <div className='text-sm'>{t('common.tryAdjusting')}</div>
                </div>
              </div>
            )}

            {recentSearchesBlock}
            {showEmptyState && (
              <div className='text-muted-foreground px-2 py-1 text-xs font-semibold tracking-wide uppercase'>
                {t('search.suggestions')}
              </div>
            )}
            <div className='space-y-4'>
              {searchCategories.length > 0 ? (
                searchCategories.map((category, categoryIndex) => (
                  <div key={category.type}>
                    <div className='text-muted-foreground px-2 py-1 text-xs font-semibold tracking-wide uppercase'>
                      {category.title}
                    </div>
                    <div className='space-y-1'>
                      {category.items.map((item, itemIndex) => {
                        const globalIndex =
                          searchCategories
                            .slice(0, categoryIndex)
                            .reduce((acc, cat) => acc + cat.items.length, 0) + itemIndex;

                        const formattedResult =
                          'type' in item && item.type !== 'navigation'
                            ? searchService.formatSearchResult(item as SearchResult)
                            : { title: item.title, description: item.description };

                        return (
                          <Button
                            key={item.id}
                            variant='ghost'
                            className={cn(
                              'h-auto w-full justify-start gap-3 p-3',
                              globalIndex === selectedIndex && 'bg-secondary'
                            )}
                            onClick={() => handleSelect(item)}
                            data-search-index={globalIndex}
                          >
                            <div className='text-muted-foreground'>{item.icon}</div>
                            <div className='flex min-w-0 flex-1 flex-col items-start gap-1'>
                              <div className='w-full truncate font-medium'>
                                {formattedResult.title}
                              </div>
                              {formattedResult.description && (
                                <div className='text-muted-foreground w-full truncate text-sm'>
                                  {formattedResult.description}
                                </div>
                              )}
                              {'subtitle' in formattedResult && formattedResult.subtitle && (
                                <div className='text-muted-foreground/80 w-full truncate text-xs'>
                                  {formattedResult.subtitle}
                                </div>
                              )}
                            </div>
                            {'type' in item && item.type !== 'navigation' && (
                              <div className='text-muted-foreground bg-secondary rounded px-2 py-1 text-xs'>
                                {t(`search.resultTypes.${item.type}`)}
                              </div>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                // Fallback to original navigation-only results when no categories
                <div className='space-y-1'>
                  {filteredItems.map((item, index) => (
                    <Button
                      key={item.id}
                      variant='ghost'
                      className={cn(
                        'h-auto w-full justify-start gap-3 p-3',
                        index === selectedIndex && 'bg-secondary'
                      )}
                      onClick={() => handleSelect(item)}
                      data-search-index={index}
                    >
                      <div className='text-muted-foreground'>{item.icon}</div>
                      <div className='flex flex-col items-start gap-1'>
                        <div className='font-medium'>{item.title}</div>
                        {item.description && (
                          <div className='text-muted-foreground text-sm'>{item.description}</div>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {viewAllFooter}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl p-0'>
        <DialogHeader className='sr-only'>
          <DialogTitle>{t('search.title')}</DialogTitle>
        </DialogHeader>
        <div className='flex items-center gap-3 border-b px-4 py-3'>
          <Search size={20} className='text-muted-foreground' />
          <Input
            ref={desktopInputRef}
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className='border-0 bg-transparent focus-visible:ring-0'
            autoFocus
          />
        </div>
        <div className='max-h-96 overflow-y-auto p-2'>
          {isLoading && (
            <div className='text-muted-foreground flex items-center justify-center gap-2 p-4'>
              <Loader2 size={16} className='animate-spin' />
              <span>{t('search.searching')}</span>
            </div>
          )}

          {error && (
            <div className='text-destructive flex items-center justify-center gap-2 p-4'>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {!isLoading && !error && totalResults === 0 && query.length >= 2 && (
            <div className='text-muted-foreground flex flex-col items-center justify-center gap-2 p-8'>
              <Search size={32} />
              <div className='text-center'>
                <div className='font-medium'>{t('search.noResults')}</div>
                <div className='text-sm'>{t('common.tryAdjusting')}</div>
              </div>
            </div>
          )}

          {recentSearchesBlock}
          {showEmptyState && (
            <div className='text-muted-foreground px-2 py-1 text-xs font-semibold tracking-wide uppercase'>
              {t('search.suggestions')}
            </div>
          )}
          <div className='space-y-3'>
            {searchCategories.length > 0 ? (
              searchCategories.map((category, categoryIndex) => (
                <div key={category.type}>
                  <div className='text-muted-foreground px-2 py-1 text-xs font-semibold tracking-wide uppercase'>
                    {category.title}
                  </div>
                  <div className='space-y-1'>
                    {category.items.map((item, itemIndex) => {
                      const globalIndex =
                        searchCategories
                          .slice(0, categoryIndex)
                          .reduce((acc, cat) => acc + cat.items.length, 0) + itemIndex;

                      const formattedResult =
                        'type' in item && item.type !== 'navigation'
                          ? searchService.formatSearchResult(item as SearchResult)
                          : { title: item.title, description: item.description };

                      return (
                        <Button
                          key={item.id}
                          variant='ghost'
                          className={cn(
                            'h-auto w-full justify-start gap-3 p-3',
                            globalIndex === selectedIndex && 'bg-secondary'
                          )}
                          onClick={() => handleSelect(item)}
                          data-search-index={globalIndex}
                        >
                          <div className='text-muted-foreground'>{item.icon}</div>
                          <div className='flex min-w-0 flex-1 flex-col items-start gap-1'>
                            <div className='w-full truncate font-medium'>
                              {formattedResult.title}
                            </div>
                            {formattedResult.description && (
                              <div className='text-muted-foreground w-full truncate text-sm'>
                                {formattedResult.description}
                              </div>
                            )}
                            {'subtitle' in formattedResult && formattedResult.subtitle && (
                              <div className='text-muted-foreground/80 w-full truncate text-xs'>
                                {formattedResult.subtitle}
                              </div>
                            )}
                          </div>
                          {'type' in item && item.type !== 'navigation' && (
                            <div className='text-muted-foreground bg-secondary rounded px-2 py-1 text-xs'>
                              {t(`search.resultTypes.${item.type}`)}
                            </div>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              // Fallback to original navigation-only results when no categories
              <div className='space-y-1'>
                {filteredItems.map((item, index) => (
                  <Button
                    key={item.id}
                    variant='ghost'
                    className={cn(
                      'h-auto w-full justify-start gap-3 p-3',
                      index === selectedIndex && 'bg-secondary'
                    )}
                    onClick={() => handleSelect(item)}
                    data-search-index={index}
                  >
                    <div className='text-muted-foreground'>{item.icon}</div>
                    <div className='flex flex-col items-start gap-1'>
                      <div className='font-medium'>{item.title}</div>
                      {item.description && (
                        <div className='text-muted-foreground text-sm'>{item.description}</div>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>
          {viewAllFooter}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchCommand;
